import React, { useState } from "react";
import { Box, ScrollArea } from "@mantine/core";
import {
  DndContext,
  DragOverlay,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import WorkflowColumn from "./WorkflowColumn";
import WorkflowCard from "./WorkflowCard";
import { moveTask } from "../../services/workflowService";
import socketService from "../../services/socketService";

const WorkflowBoard = ({
  tasks,
  columns,
  users,
  onTaskUpdate,
  onTaskClick,
}) => {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  React.useEffect(() => {
    socketService.connect();
    // Global socket listeners (board:refresh) refactored to Workflow.js for better synchronization
  }, []);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeTask = tasks.find((t) => Number(t.id) === Number(active.id));
    if (!activeTask) {
      setActiveId(null);
      return;
    }

    let overColumnId = over.data?.current?.columnId;
    let overTaskId = null;

    if (!overColumnId) {
      const overTask = tasks.find((t) => Number(t.id) === Number(over.id));
      if (overTask) {
        overColumnId = Number(overTask.columnId);
        overTaskId = Number(overTask.id);
      } else {
        const overIdStr = String(over.id);
        if (overIdStr.startsWith("column-")) {
          overColumnId = Number(overIdStr.replace("column-", ""));
        } else {
          overColumnId = Number(over.id);
        }
      }
    } else if (
      typeof over.id === "number" ||
      (typeof over.id === "string" && !isNaN(over.id))
    ) {
      const overTask = tasks.find((t) => Number(t.id) === Number(over.id));
      if (overTask) overTaskId = Number(overTask.id);
      overColumnId = Number(overColumnId);
    }

    let newPosition = 1;
    const columnIdToFilter = Number(overColumnId);
    const columnTasks = tasks
      .filter(
        (t) =>
          Number(t.columnId) === columnIdToFilter &&
          Number(t.id) !== Number(active.id),
      )
      .sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.createdAt) - new Date(b.createdAt),
      );

    if (overTaskId) {
      const overTaskIndex = columnTasks.findIndex((t) => t.id === overTaskId);
      newPosition = overTaskIndex + 1;
    } else {
      newPosition = columnTasks.length + 1;
    }

    // --- OPTIMISTIC UPDATE ---
    // 1. Save original tasks for rollback
    const originalTasks = [...tasks];

    // 2. Perform optimistic state change
    const updatedTasks = tasks.map((t) => {
      if (t.id === activeTask.id) {
        return { ...t, columnId: overColumnId, position: newPosition };
      }
      return t;
    });

    // Update positions of other tasks in the target column
    updatedTasks.forEach((t) => {
      if (
        t.id !== activeTask.id &&
        t.columnId === overColumnId &&
        t.position >= newPosition
      ) {
        t.position += 1;
      }
    });

    onTaskUpdate(true, updatedTasks); // Pass optimistic tasks to parent
    // -------------------------

    try {
      await moveTask(activeTask.id, overColumnId, newPosition);
    } catch (error) {
      console.error("Error moving task, reverting:", error);
      onTaskUpdate(true, originalTasks); // Rollback on error
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeTask = tasks.find((t) => t.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <ScrollArea>
        <Box
          style={{
            display: "inline-flex",
            minWidth: "100%",
            gap: "12px",
            // padding: "24px",
            minHeight: "calc(100vh - 200px)",
            // background: "rgba(25, 79, 132, 0.5)",

            borderRadius: "7px",
            margin: "0",
            // border: "1px solid rgba(233, 236, 239, 0.8)",
          }}
        >
          <SortableContext
            items={columns.map((col) => `column-${col.id}`)}
            strategy={horizontalListSortingStrategy}
          >
            {columns.map((column) => {
              const columnTasks = tasks.filter(
                (task) => task.columnId === column.id,
              );

              return (
                <WorkflowColumn
                  key={column.id}
                  column={column}
                  tasks={columnTasks}
                  users={users}
                  onTaskUpdate={onTaskUpdate}
                  onTaskClick={onTaskClick}
                />
              );
            })}
          </SortableContext>
        </Box>
      </ScrollArea>

      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <Box
            style={{
              transform: "rotate(3deg) scale(1.05)",
              cursor: "grabbing",
              zIndex: 1000,
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <WorkflowCard task={activeTask} isDragging users={users} />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default WorkflowBoard;
